/**
 * In-app error/crash log.
 *
 * The simulator runs offline on an unattended learner screen, so there is no
 * dev console to inspect when something breaks. This module captures every
 * error (window errors, unhandled rejections, console.error/warn, and React
 * error-boundary catches) into a ring buffer, each entry tagged with a compact
 * snapshot of the sim state (scenario / rhythm / vitals) — which is usually
 * what explains the crash. The DebugOverlay renders and copies this buffer.
 */
import { useSimStore } from '../store/useSimStore';

const MAX_ENTRIES = 80;
const entries = [];
const listeners = new Set();
let installed = false;

// Build timestamp injected by Vite (see vite.config.js). Ties a log to a deploy.
export const BUILD_TIME = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'dev';

// Notify subscribers, but ALWAYS asynchronously. A logEvent can fire from inside
// React's render phase (e.g. a framework console.warn is mirrored here), and a
// subscriber that setStates — like the DebugOverlay badge — must never run during
// render or React throws "Cannot update a component while rendering a different
// component", which cascades into an error-boundary loop. Deferring to a macrotask
// guarantees the notification lands after the current render/commit completes.
let emitScheduled = false;
function emit() {
  if (emitScheduled) return;
  emitScheduled = true;
  setTimeout(() => {
    emitScheduled = false;
    listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
  }, 0);
}

/** Compact snapshot of the sim — the context that usually explains a crash. */
function stateSnapshot() {
  try {
    const s = useSimStore.getState();
    const v = s.vitals || {};
    const r = (x) => (typeof x === 'number' ? Math.round(x * 100) / 100 : x);
    return {
      scenario: s.activeScenario, modifier: s.activeModifier, rhythm: s.rhythm,
      capno: s.capnoMorphology, split: s.splitScreen,
      HR: r(v.HR?.current), RR: r(v.RR?.current), SpO2: r(v.SpO2?.current), EtCO2: r(v.EtCO2?.current),
    };
  } catch {
    return null;
  }
}

/** Record an event. `extra` may include { stack, componentStack, source }. */
export function logEvent(type, message, extra = {}) {
  entries.push({
    t: Date.now(),
    type,
    message: String(message).slice(0, 2000),
    route: typeof location !== 'undefined' ? location.pathname + location.search : '',
    state: stateSnapshot(),
    ...extra,
  });
  if (entries.length > MAX_ENTRIES) entries.shift();
  emit();
}

export function getEntries() { return entries.slice().reverse(); }   // newest first
export function clearEntries() { entries.length = 0; emit(); }
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }

/** Plain-text dump for copy/paste. */
export function formatEntries() {
  const header = `Anesthesia Sim — crash log\nbuild: ${BUILD_TIME}\nurl: ${typeof location !== 'undefined' ? location.href : ''}\nua: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}\n`;
  const body = getEntries().map((e) => {
    const time = new Date(e.t).toISOString();
    const st = e.state ? ` | ${JSON.stringify(e.state)}` : '';
    const stack = e.componentStack ? `\n  reactStack:${e.componentStack}` : e.stack ? `\n  ${String(e.stack).split('\n').slice(0, 6).join('\n  ')}` : '';
    return `[${time}] ${e.type} @ ${e.route}${st}\n  ${e.message}${stack}`;
  }).join('\n\n');
  return `${header}\n${body || '(no entries)'}`;
}

/** Install global capture. Safe to call once at startup. */
export function installGlobalErrorCapture() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e) => {
    logEvent('window.error', e.message || 'error', {
      stack: e.error?.stack,
      source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined,
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    logEvent('unhandledrejection', (r && r.message) || String(r), { stack: r && r.stack });
  });

  // Mirror console.error / console.warn into the log (does not recurse — logEvent
  // never calls console). Captures React warnings and our own logging.
  ['error', 'warn'].forEach((level) => {
    const orig = console[level].bind(console);
    console[level] = (...args) => {
      orig(...args);
      try {
        const msg = args
          .map((a) => (a instanceof Error ? a.stack || a.message : typeof a === 'object' ? safeJson(a) : String(a)))
          .join(' ');
        logEvent(`console.${level}`, msg);
      } catch { /* ignore */ }
    };
  });
}

function safeJson(o) { try { return JSON.stringify(o); } catch { return String(o); } }
